import { Bookmark, Home, Settings, User, type LucideIcon } from 'lucide-react';
import { MODULES } from '@/lib/modules';
import { ADMIN_SECTIONS, MODULE_MENUS } from '@/lib/navigation';
import { ROUTES } from '@/lib/routes';

/**
 * Page search — the index behind the header's search box and Home's "jump to". Pages only
 * (by decision): it's built entirely from lib/navigation.ts + the module registry, so any page
 * added to the menus is searchable with no extra work. A future true global search (devices,
 * users, tickets…) should be a separate, server-backed thing — don't bolt records onto this.
 */

export interface PageDestination {
  /** Module label ("PSA", "RMM"…), "Admin", or "Cosmos". */
  group: string;
  /** Column / section within the group ("Tickets", "Dashboards"…). */
  section: string;
  label: string;
  to: string;
  icon: LucideIcon;
  /** Extra words that should match (vendor name etc.). */
  keywords: string;
}

/** Every page the user can reach. Admin pages only for admins. */
export function pageDestinations(isAdmin: boolean): PageDestination[] {
  const out: PageDestination[] = [
    { group: 'Cosmos', section: 'General', label: 'Home', to: ROUTES.home, icon: Home, keywords: 'start landing modules' },
  ];
  for (const m of MODULES.filter((x) => x.status === 'live')) {
    out.push({ group: m.label, section: m.vendor, label: `${m.label} overview`, to: m.to, icon: m.icon, keywords: `${m.blurb} landing module` });
    for (const col of MODULE_MENUS[m.id as keyof typeof MODULE_MENUS] ?? []) {
      for (const i of col.items) {
        if (i.kind !== 'link') continue;
        out.push({ group: m.label, section: col.title, label: i.label, to: i.to, icon: m.icon, keywords: m.vendor });
      }
    }
  }
  out.push(
    { group: 'Account', section: 'Account', label: 'Account info', to: ROUTES.account.info, icon: User, keywords: 'profile me sign-in' },
    { group: 'Account', section: 'Account', label: 'Note templates', to: ROUTES.account.noteTemplates, icon: Settings, keywords: 'canned replies' },
    { group: 'Account', section: 'Account', label: 'Bookmarks', to: ROUTES.account.bookmarks, icon: Bookmark, keywords: 'favourites saved pages star' },
    { group: 'Account', section: 'Account', label: 'Preferences', to: ROUTES.account.preferences, icon: Settings, keywords: 'sidebar navigation start page landing density compact personalise customise' },
  );
  if (isAdmin) {
    for (const s of ADMIN_SECTIONS) {
      for (const i of s.items) out.push({ group: 'Admin', section: s.title, label: i.label, to: i.to, icon: i.icon, keywords: 'admin settings configuration' });
    }
  }
  // Same route listed twice (e.g. a module landing that is also a menu item) — keep the first.
  const seen = new Set<string>();
  return out.filter((d) => {
    const k = `${d.to}|${d.label}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/**
 * Every term must appear somewhere (label, section, group, keywords). Ranked: label starts with
 * the query > a label word starts with it > label contains it > only context matched.
 */
export function searchPages(list: PageDestination[], query: string, limit = 12): PageDestination[] {
  const q = query.trim().toLowerCase();
  const terms = q.split(/\s+/).filter(Boolean);
  if (!terms.length) return [];
  const scored: { d: PageDestination; score: number }[] = [];
  for (const d of list) {
    const label = d.label.toLowerCase();
    const hay = `${label} ${d.section} ${d.group} ${d.keywords}`.toLowerCase();
    if (!terms.every((t) => hay.includes(t))) continue;
    const score = label.startsWith(q) ? 0
      : label.split(/[\s—&·/-]+/).some((w) => w.startsWith(terms[0])) ? 1
      : label.includes(terms[0]) ? 2
      : 3;
    scored.push({ d, score });
  }
  return scored.sort((a, b) => a.score - b.score || a.d.label.length - b.d.label.length).slice(0, limit).map((x) => x.d);
}

